import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { TailSpin } from "react-loader-spinner";
import Button from "../../components/flat/ui/Button";
import { Password } from "../../components/Password";
import AuthPasswordLayout, {
  AUTH_ILLUSTRATIONS,
  AuthLoginFooter,
  AuthPasswordSuccessView,
  ButtonText,
  Container,
  Div,
  ErrorMessage,
  Form,
  FormField,
  PanelIllustration,
  ReadOnlyInput,
  RequiredFieldLabel,
  StyledInput,
  Subtitle,
  Title,
} from "../Auth/components/AuthPasswordLayout";
import {
  trackPasswordResetAttemptStarted,
  trackPasswordResetPageDisplayed,
  trackPasswordResetSuccessful,
} from "../Auth/trackPasswordEvents";
import { PasswordResetSchema } from "./schema";
import type { RootStateWithResetPassword } from "./resetPassword.types";

interface PasswordResetFormValues {
  password: string;
  confirmation_password: string;
}

export default function PasswordReset() {
  const dispatch = useDispatch();
  const { token = "" } = useParams<{ token: string }>();
  const location = useLocation();
  const emailFromQuery = new URLSearchParams(location.search).get("email") || "";

  const loadingResetPassword = useSelector(
    (state: RootStateWithResetPassword) =>
      state.resetPassword.loading_reset_password
  );
  const resetPasswordData = useSelector(
    (state: RootStateWithResetPassword) =>
      state.resetPassword.reset_password_data
  );

  const isSuccess =
    Object.keys(resetPasswordData).length > 0 &&
    Boolean(resetPasswordData.success);
  const isError =
    Object.keys(resetPasswordData).length > 0 && !resetPasswordData.success;

  useEffect(() => {
    trackPasswordResetPageDisplayed();
  }, []);

  useEffect(() => {
    if (isSuccess) {
      trackPasswordResetSuccessful();
    }
  }, [isSuccess]);

  const form = useFormik<PasswordResetFormValues>({
    initialValues: {
      password: "",
      confirmation_password: "",
    },
    validationSchema: PasswordResetSchema,
    onSubmit: values => {
      trackPasswordResetAttemptStarted();
      dispatch({
        type: "RESET_PASSWORD",
        payload: {
          data: {
            password: values.password,
            confirmation_password: values.confirmation_password,
            token,
          },
        },
      });
    },
  });

  const errorMessage =
    typeof resetPasswordData.msg === "string"
      ? resetPasswordData.msg
      : Array.isArray(resetPasswordData.msg)
        ? resetPasswordData.msg.join(" ")
        : "Ocorreu um erro ao alterar sua senha. Tente novamente.";

  return (
    <AuthPasswordLayout
      rightContent={
        <PanelIllustration src={AUTH_ILLUSTRATIONS.INSERT_PASSWORD} alt="" />
      }
    >
      <Container>
        {isSuccess ? (
          <AuthPasswordSuccessView
            title="Senha redefinida com sucesso"
            subtitle="Agora você já pode entrar na Jusfy com sua nova senha."
            buttonLabel="Voltar para login"
            buttonTo="/auth/login"
          />
        ) : (
          <>
            <Div>
              <Title>Redefinição de senha</Title>
              <Subtitle>
                Cadastre uma nova senha para continuar acessando a plataforma
                Jusfy.
              </Subtitle>
            </Div>

            {isError && <ErrorMessage>{errorMessage}</ErrorMessage>}

            <Form onSubmit={form.handleSubmit}>
              {emailFromQuery && (
                <FormField>
                  <RequiredFieldLabel htmlFor="email">E-mail</RequiredFieldLabel>
                  <ReadOnlyInput
                    id="email"
                    type="email"
                    value={emailFromQuery}
                    readOnly
                    tabIndex={-1}
                  />
                </FormField>
              )}

              <FormField>
                <RequiredFieldLabel htmlFor="password">
                  Nova senha
                </RequiredFieldLabel>
                <Password
                  id="password"
                  form={form}
                  showToggle
                  variant="checklist"
                />
              </FormField>

              <FormField>
                <RequiredFieldLabel htmlFor="confirmation_password">
                  Confirmar senha
                </RequiredFieldLabel>
                <StyledInput
                  id="confirmation_password"
                  type="password"
                  name="confirmation_password"
                  autoComplete="new-password"
                  value={form.values.confirmation_password}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                />
                {form.touched.confirmation_password &&
                  form.errors.confirmation_password && (
                    <ErrorMessage>
                      {form.errors.confirmation_password}
                    </ErrorMessage>
                  )}
              </FormField>

              <Button
                type="submit"
                width="100%"
                padding="10px 40px"
                borderRadius="5px"
                hoverBackgroundColor="#007656"
                disabled={loadingResetPassword}
              >
                <ButtonText>
                  {loadingResetPassword ? (
                    <TailSpin
                      visible
                      height="20"
                      width="20"
                      color="#ffffff"
                      ariaLabel="loading"
                    />
                  ) : (
                    "Salvar nova senha"
                  )}
                </ButtonText>
              </Button>

              <AuthLoginFooter />
            </Form>
          </>
        )}
      </Container>
    </AuthPasswordLayout>
  );
}
